import { Component, OnInit, Input, Output, EventEmitter, TemplateRef, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { ActivateLicense, ActivationResponse, Activations } from "../../models/activations";
import { UserDetails } from "../../models/user";
import { PortalService } from "../../services/portal-service.service";
import { MatDialog } from "@angular/material/dialog";
import { BrowserStorage } from "../../core/storage";
import { CristieUtils } from "../../core/utils.service";

@Component({
  standalone: false,
  selector: "app-activation-details",
  templateUrl: "./activation-details.component.html",
  styleUrls: ["./activation-details.component.scss"],
})
export class ActivationDetailsComponent implements OnInit {
  @ViewChild("confirmModal") confirmModalRef: TemplateRef<any>;
  @ViewChild("activationModal") activationModalRef: TemplateRef<any>;
  @Input() activations: Activations[];
  @Input() showSurrender: false;
  @Input() showRenew: false;
  @Input() contractId: number = null;
  @Input() product: string = null;
  @Input() rental = false;
  @Input() returnsRemaining: number | null = null;
  @Input() loading = false;
  @Input() showComments = false;
  @Input() showLicenseDetails = true;
  @Output() surrenderSelected = new EventEmitter<void>();

  ActivationsForm: UntypedFormGroup;
  SurrenderForm: UntypedFormGroup;
  userData = new UserDetails();
  criteria: string;
  activationResponse = new Array<ActivationResponse>();
  success = false;
  error: any = null;
  renewing = false;
  exporting = false;
  sortKey: "hostname" | "type" | "comment" | "obtained" | "expiry" = "obtained";
  sortDirection: "asc" | "desc" = "desc";
  showPhysical = true;
  showVirtual = true;
  showDesktop = true;
  showTokens = true;

  get showActivationCodes(): boolean {
    return BrowserStorage.getBoolean("activation-show-codes", false, false) ?? false;
  }

  get showActivationCodeColumn(): boolean {
    return this.showLicenseDetails && !this.showSurrender && this.showActivationCodes;
  }

  get showActionColumn(): boolean {
    return !this.showSurrender && this.showLicenseDetails;
  }

  constructor(
    private fb: UntypedFormBuilder,
    private ps: PortalService,
    private dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.showPhysical = BrowserStorage.getBoolean("activation-filter-physical", true, false) ?? true;
    this.showVirtual = BrowserStorage.getBoolean("activation-filter-virtual", true, false) ?? true;
    this.showDesktop = BrowserStorage.getBoolean("activation-filter-desktop", true, false) ?? true;
    this.showTokens = BrowserStorage.getBoolean("activation-filter-token", true, false) ?? true;

    this.ActivationsForm = this.fb.group({
      activations: this.activations,
      filterActivations: "",
      returns: this.fb.array([]),
    });
  }

  get selectedCount() {
    return (this.activations || []).filter((activation) => activation.selected).length;
  }

  get selectedActivations() {
    return (this.activations || []).filter((activation) => activation.selected);
  }

  get activationColumnCount(): number {
    return 3 + (this.showComments ? 1 : 0) + (this.showActivationCodeColumn ? 1 : 0) + (this.rental ? 1 : 0) + (this.showActionColumn ? 1 : 0) + (this.showSurrender ? 1 : 0);
  }

  get displayedActivations(): Activations[] {
    const filter = (this.criteria || "").trim().toLowerCase();
    const items = (this.activations || []).filter((activation) => {
      if (!this.isTypeVisible(activation)) return false;
      if (!filter) return true;

      const searchableValues = [
        activation.hostname,
        activation.code,
        activation.comment,
        activation.ipAddress,
        this.getActivationTypeLabel(activation),
        activation.timestamp
          ? new Date(activation.timestamp).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })
          : "",
        activation.expiry
          ? new Date(activation.expiry).toLocaleDateString("en-GB", {
              day: "2-digit",
              month: "long",
              year: "numeric",
            })
          : "",
      ];

      return searchableValues.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(filter),
      );
    });

    return [...items].sort((a, b) => this.compareActivations(a, b));
  }

  confirmRenewal() {
    this.dialog.open(this.confirmModalRef, { width: "960px" });
  }

  sortBy(key: "hostname" | "type" | "comment" | "obtained" | "expiry"): void {
    if (this.sortKey === key) {
      this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      return;
    }

    this.sortKey = key;
    this.sortDirection = key === "obtained" || key === "expiry" ? "desc" : "asc";
  }

  getSortIcon(key: "hostname" | "type" | "comment" | "obtained" | "expiry"): string {
    if (this.sortKey !== key) return "unfold_more";
    return this.sortDirection === "asc" ? "arrow_upward" : "arrow_downward";
  }

  closeDialog(): void {
    this.dialog.closeAll();
  }

  setTypeVisibility(key: "physical" | "virtual" | "desktop" | "token", value: boolean): void {
    switch (key) {
      case "physical":
        this.showPhysical = value;
        break;
      case "virtual":
        this.showVirtual = value;
        break;
      case "desktop":
        this.showDesktop = value;
        break;
      case "token":
        this.showTokens = value;
        break;
    }

    BrowserStorage.setBoolean(`activation-filter-${key}`, value, false);
  }

  confirmRenewLicenses(): void {
    if (this.renewing) return;
    this.closeDialog();
    this.renewLicenses();
  }

  resetMessage() {
    this.success = false;
    this.error = null;
  }

  renewLicenses() {
    if (this.renewing) return;
    this.resetMessage();
    const selected = this.selectedActivations;
    if (selected.length === 0) return;

    this.renewing = true;
    let remaining = selected.length;
    const finishRequest = () => {
      remaining -= 1;
      if (remaining === 0) {
        this.renewing = false;
      }
    };

    for (const a of selected) {
      const requests = [];
      const act = new ActivateLicense();
      act.machineType = a.licType.toUpperCase();
      act.product = a.product.toUpperCase();
      act.platform = a.platform;
      act.signature = a.signature;
      act.attribute = a.attribute;
      act.comment = `${a.comment}; Bulk renewal by ${CristieUtils.decodeBase64(localStorage.getItem("name"))} `;
      act.ip = a.ipAddress;
      act.hostname = a.hostname;
      requests.push(act);
      this.ps.ActivateLicense(requests, this.contractId).subscribe(
        (response) => {
          this.success = true;
          this.activationResponse = response;
          finishRequest();
          this.dialog.open(this.activationModalRef, { width: "1200px", maxWidth: "95vw" });
        },
        (error) => {
          this.error = error;
          finishRequest();
        },
      );
    }
  }

  copyActivationDetails() {
    const entries = this.activationResponse || [];

    if (!entries.length) {
      return;
    }

    const lines = [
      "HOSTNAME\tKEY\tCOMMAND",
      ...entries.map((e) => `${e.hostname || ""}\t${e.code || ""}\t${this.getActivationResponseLicenseCommand(e)}`),
    ];

    const textToCopy = lines.join("\n");
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => console.log("Copied activation details!"))
      .catch((err) => console.error("Failed to copy activation details", err));
  }

  copyActivationCode(code: string): void {
    if (!code) return;
    navigator.clipboard.writeText(code);
  }

  getActivationResponseLicenseCommand(entry: ActivationResponse): string {
    const source = this.getActivationResponseSource(entry);
    const product = String(source?.product || this.product || "").toUpperCase();
    const key = entry?.code || "";
    if (this.isWindowsPlatform(source?.platform)) {
      return `licmgr.exe /p ${product} /act ${key}`;
    }

    return `licmgr -p ${product} --act ${key}`;
  }

  copyActivationResponseLicenseCommand(entry: ActivationResponse): void {
    navigator.clipboard.writeText(this.getActivationResponseLicenseCommand(entry));
  }

  copyLicenseCommand(act: Activations): void {
    if (!this.canCopyLicenseCommand(act)) return;
    navigator.clipboard.writeText(this.getLicenseCommand(act));
  }

  canCopyActivationKey(act: Activations): boolean {
    return this.showLicenseDetails && this.isBMRActivation(act);
  }

  canCopyLicenseCommand(act: Activations): boolean {
    return this.showLicenseDetails && this.isBMRActivation(act);
  }

  getLicenseCommand(act: Activations): string {
    const product = String(act.product || "").toUpperCase();
    const key = act.code || "";
    if (this.isWindowsActivation(act)) {
      return `licmgr.exe /p ${product} /act ${key}`;
    }

    return `licmgr -p ${product} --act ${key}`;
  }

  shouldShowTooltip(value: string | null | undefined, threshold: number): boolean {
    return String(value || "").length > threshold;
  }

  async exportActivations() {
    if (this.exporting) return;
    this.exporting = true;
    try {
      const res = await this.ps.exportActivations(this.contractId);
      const blob = new Blob([res], { type: "application/*" });
      const a = document.createElement("a");
      a.href = (window.URL || (window as any).webkitURL).createObjectURL(blob);
      a.target = "_blank";
      a.download = `activations-${this.contractId}.tsv`;
      document.body.appendChild(a);
      a.click();
    } finally {
      this.exporting = false;
    }
  }

  triggerSurrender(): void {
    this.surrenderSelected.emit();
  }

  exportDisabled(): boolean {
    return this.exporting || this.activations.length === 0;
  }

  renewDisabled(): boolean {
    return this.renewing || this.selectedCount == 0;
  }

  getActivationTypeLabel(act: Activations): string {
    if (act.activationType !== "TOKEN") {
      return `${act.licType?.toLowerCase()}`;
    }

    return `${act.count} token${act.count === 0 || act.count > 1 ? "s" : ""}`;
  }

  hasActivationType(type: "physical" | "virtual" | "desktop" | "token"): boolean {
    return (this.activations || []).some((activation) => this.getActivationTypeKey(activation) === type);
  }

  getActivationTypeCount(type: "physical" | "virtual" | "desktop" | "token"): number {
    return (this.activations || []).filter((activation) => this.getActivationTypeKey(activation) === type).length;
  }

  private compareActivations(a: Activations, b: Activations): number {
    const direction = this.sortDirection === "asc" ? 1 : -1;

    switch (this.sortKey) {
      case "hostname":
        return direction * this.compareText(a.hostname, b.hostname);
      case "type":
        return direction * this.compareText(this.getActivationTypeLabel(a), this.getActivationTypeLabel(b));
      case "comment":
        return direction * this.compareText(a.comment, b.comment);
      case "expiry":
        return direction * this.compareDate(a.expiry, b.expiry);
      case "obtained":
      default:
        return direction * this.compareDate(a.timestamp, b.timestamp);
    }
  }

  private compareText(a: unknown, b: unknown): number {
    return String(a || "").localeCompare(String(b || ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }

  private compareDate(a: Date | string, b: Date | string): number {
    return new Date(a || 0).getTime() - new Date(b || 0).getTime();
  }

  private isTypeVisible(activation: Activations): boolean {
    switch (this.getActivationTypeKey(activation)) {
      case "physical":
        return this.showPhysical;
      case "virtual":
        return this.showVirtual;
      case "desktop":
        return this.showDesktop;
      case "token":
        return this.showTokens;
      default:
        return true;
    }
  }

  private getActivationTypeKey(activation: Activations): "physical" | "virtual" | "desktop" | "token" {
    if (activation.activationType === "TOKEN") return "token";
    const type = String(activation.licType || "").toLowerCase();
    if (type.includes("virtual")) return "virtual";
    if (type.includes("desktop")) return "desktop";
    return "physical";
  }

  private isBMRActivation(act: Activations): boolean {
    return act.activationType !== "TOKEN" && ["ABMR", "CBMR", "COBMR", "NBMR", "PBMR", "RBMR", "TBMR"].includes(String(act.product || "").toUpperCase());
  }

  private isWindowsActivation(act: Activations): boolean {
    return this.isWindowsPlatform(act.platform);
  }

  private isWindowsPlatform(platform: string): boolean {
    return String(platform || "").toUpperCase().includes("WINDOWS");
  }

  private getActivationResponseSource(entry: ActivationResponse): Activations | undefined {
    return (this.activations || []).find((activation) => activation.hostname === entry.hostname && activation.ipAddress === entry.ipAddr)
      || (this.activations || []).find((activation) => activation.hostname === entry.hostname);
  }
}
